import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses';
import { paths } from '../../common/constants/pathVariables';
import { DialogType } from '../../common/enums';
import { useSubmitReview } from '../../common/hooks/useSubmitReview';
import { notMandatoryMsg, t } from '../../common/i18n';
import { Checkbox } from '../../common/react-hook-form/components/Checkbox';
import { BillingAccount } from '../../common/react-hook-form/fields/BillingAccount';
import { CostCenter } from '../../common/react-hook-form/fields/CostCenter';
import { Reference } from '../../common/react-hook-form/fields/Reference';
import { getDefaultDeliveryOrder } from '../../common/utils/orderUtils';
import { CustomerOrderStatus, PostReviewRequest, SubscriptionStatusType } from '../../generated/api/models.js';
import { openDialog } from '../../public/site/dialogStore.js';
import { ApproveOrDeny } from '../ApproveOrDeny/ApproveOrDeny';
import { isSfdcOrder } from '../CustomerOrderList/customerOrderUtils.js';
import { ChangeRequestInfo } from './ChangeRequestInfo';
import { CustomerOrderCustomerInfo } from './CustomerOrderCustomerInfo.js';
import { CustomerOrderDeviceSubscriptions } from './CustomerOrderDeviceSubscriptions.js';
import { CustomerOrderInfo } from './CustomerOrderInfo.js';
import { CustomerOrderOrderedProductsList } from './CustomerOrderOrderedProductsList.js';
import { EppDeviceChangeSummaries } from './EppDeviceChange/EppDeviceChangeSummaries.js';
import { EppPendingRedeemDisclaimers } from './EppDeviceChange/EppPendingRedeemDisclaimers.js';
import { OECOrderInfo } from './OECOrderInfo.js';
import type { ReviewOnlineOrderDialogParams } from '../../common/types/dialog';
import type {
  BillingAccountSearchResponse,
  ChangeRequestDetails,
  ContactHeader,
  CustomerOrder,
  Subscription,
} from '../../generated/api/models.js';

import './CustomerOrderDetails.scss';

interface CustomerOrderContentParams {
  pathTemplate: string;
  customerOrder?: CustomerOrder;
  changeRequestDetails?: ChangeRequestDetails;
  ordererContact: ContactHeader | undefined;
  deviceSubscriptions: Subscription[] | undefined;
  isInternalCustomer: boolean;
  billingAccounts?: BillingAccountSearchResponse[];
  mdmId: string;
  customerOrderDescription?: string;
}

// userContactId will be same for all deliverOrder when an employee places an order.
const getEmployeeUserContactId = (customerOrder: CustomerOrder): string | undefined =>
  customerOrder?.deliveryOrders.find(deliveryOrder => deliveryOrder.userContactId)?.userContactId;

const activeEppDeviceSubscriptionsOfEmployee = (
  customerOrdersDetail: CustomerOrder,
  deviceSubscriptions?: Subscription[]
): Subscription[] | undefined => {
  const employeeUserContactId = getEmployeeUserContactId(customerOrdersDetail);
  return deviceSubscriptions?.filter(
    item =>
      item.details?.device?.eppSubscription &&
      item.subscriptionContactId === employeeUserContactId &&
      item.subscriptionStatus === SubscriptionStatusType.ACTIVE
  );
};

const findFirstReplacedSubscriptionDisplayId = (customerOrder: CustomerOrder) =>
  customerOrder?.deliveryOrders?.find(deliveryOrder => !!deliveryOrder.replacedSubscriptionDisplayId)
    ?.replacedSubscriptionDisplayId;

export interface CustomerOrderDetailsFormValues {
  costCenter: string;
  employeeNumber: string;
  billingAccountId: string;
  orderAndBillingDetailsChecked: boolean;
  approved?: boolean;
}

export interface CustomerOrderBillingDetailsFormProps {
  isInternalCustomer: boolean;
  billingAccounts: BillingAccountSearchResponse[];
  customerOrder: CustomerOrder;
  mdmId: string;
  ordererContact?: ContactHeader;
}

const getReviewOnlineOrderDialogParams = (
  formValues: CustomerOrderDetailsFormValues,
  customerOrder: CustomerOrder,
  ordererContact?: ContactHeader
): ReviewOnlineOrderDialogParams => {
  const onlineOrderReview = {
    billingAccountId: formValues.billingAccountId,
    costCenter: formValues.costCenter,
    subscriptionReference: formValues.employeeNumber,
    onlineOrderId: customerOrder?.onlineOrderId || '',
  };
  return {
    onlineOrderReview,
    approved: !!formValues.approved,
    customerOrder: customerOrder,
    type: DialogType.REVIEW_ONLINE_ORDER,
    ordererEmail: ordererContact?.email,
  };
};

export const CustomerOrderDetailsForm = ({
  billingAccounts,
  customerOrder,
  isInternalCustomer,
  ordererContact,
  mdmId,
}: CustomerOrderBillingDetailsFormProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const defaultDeliveryOrder = getDefaultDeliveryOrder(customerOrder.deliveryOrders);
  const methods = useForm<CustomerOrderDetailsFormValues>({
    defaultValues: {
      billingAccountId: customerOrder.approverMustSelectBillingAccount ? undefined : customerOrder.billingAccountId,
      costCenter: defaultDeliveryOrder?.costCenter || '',
      employeeNumber: defaultDeliveryOrder?.subscriptionReference || '',
    },
  });
  const { handleSubmit, setValue, watch, setError, reset } = methods;
  const { submitReview } = useSubmitReview(false);
  const [isSaving, setIsSaving] = useState(false);
  const selectedBillingAccountId = watch('billingAccountId');
  const orderAndBillingDetailsChecked = watch('orderAndBillingDetailsChecked');

  useEffect(() => {
    const state = location.state as { newBillingAccountDisplayId?: string } | null;

    if (state?.newBillingAccountDisplayId && billingAccounts) {
      const newBillingAccount = billingAccounts.find(
        ba => ba.result.billingAccountDisplayId === state.newBillingAccountDisplayId
      );

      if (newBillingAccount?.result.billingAccountId) {
        reset({
          billingAccountId: newBillingAccount.result.billingAccountId,
          costCenter: defaultDeliveryOrder?.costCenter ?? '',
          employeeNumber: defaultDeliveryOrder?.subscriptionReference ?? '',
          orderAndBillingDetailsChecked: false,
        });

        navigate(location.pathname + location.search, {
          replace: true,
          state: { ...state, newBillingAccountDisplayId: undefined },
        });
      }
    }
  }, [
    location.state,
    location.pathname,
    location.search,
    billingAccounts,
    reset,
    defaultDeliveryOrder,
    navigate,
    methods,
  ]);

  const onSubmit = async (formValues: CustomerOrderDetailsFormValues) => {
    if (watch('approved')) {
      const onlineOrderReview = {
        billingAccountId: formValues.billingAccountId,
        costCenter: formValues.costCenter,
        subscriptionReference: formValues.employeeNumber,
        onlineOrderId: customerOrder?.onlineOrderId ?? '',
      };

      setIsSaving(true);

      void submitReview(onlineOrderReview, PostReviewRequest.ReviewTypeEnum.ONLINE_ORDER, formValues.approved!, mdmId);
    } else {
      openDialog(getReviewOnlineOrderDialogParams(formValues, customerOrder, ordererContact));
    }
  };

  const handleApprove = (approved: boolean = false) => {
    setValue('approved', approved);

    if (approved && !selectedBillingAccountId) {
      setError('billingAccountId', { type: 'custom', message: t.GMD4('The billing account must be chosen') });
    } else if (approved && !orderAndBillingDetailsChecked) {
      setError('orderAndBillingDetailsChecked', {
        type: 'custom',
        message: t.ILA7('Confirm you have reviewed the order and billing details'),
      });
    } else {
      handleSubmit(onSubmit)();
    }
  };

  const onCreateNewBillingAccountSelected = () => {
    const options = {
      state: {
        ...location.state,
        redirectPath: `${location.pathname}?mdmId=${mdmId}`,
        mdmId,
      },
    };
    navigate(`${paths.BILLING_ACCOUNTS_CREATE_NEW_BA}?mdmId=${mdmId}`, options);
  };

  const selectedBillingAccounts = customerOrder.catalogSelectedBillingAccounts;

  const billingAccountsToDisplay = selectedBillingAccounts?.length
    ? billingAccounts.filter(ba => selectedBillingAccounts.includes(ba.result.billingAccountId!))
    : billingAccounts;

  return (
    <FormProvider {...methods}>
      <form className="of-customer-order-details-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <h3 className={dsClass.MARGIN_VERTICAL_4}>{t.W1ZW('Billing information for the products')}</h3>
        <CostCenter
          label={!isInternalCustomer ? `${t.L2OG('Cost Center')} ${t.NFRH(notMandatoryMsg)}` : undefined}
          maxLength={40}
          required={isInternalCustomer}
        />
        <Reference
          label={!isInternalCustomer ? `${t.WGGQ('e.g. employee number')} ${t.NFRH(notMandatoryMsg)}` : undefined}
          required={false}
          maxLength={80}
          hint={undefined}
        />
        <BillingAccount
          key={`billing-account-${selectedBillingAccountId ?? 'none'}`}
          billingAccounts={billingAccountsToDisplay}
          name="billingAccountId"
          enableAddNewBa={true}
          required={false}
          onCreateNewBillingAccountSelected={onCreateNewBillingAccountSelected}
        />
        <div>
          <Checkbox name="orderAndBillingDetailsChecked" required={false}>
            {t.WO28('I have checked the validity of the billing and order details.')}
          </Checkbox>
        </div>
        <ApproveOrDeny isSaving={isSaving} onApprove={() => handleApprove(true)} onDeny={() => handleApprove()} />
      </form>
    </FormProvider>
  );
};

export const CustomerOrderContent = ({
  pathTemplate,
  customerOrder,
  changeRequestDetails,
  ordererContact,
  deviceSubscriptions,
  isInternalCustomer,
  billingAccounts,
  mdmId,
  customerOrderDescription,
}: CustomerOrderContentParams) => {
  const isOecOrder = customerOrder && !isSfdcOrder(customerOrder.customerOrderDisplayId);

  if (isOecOrder) {
    return (
      <div className="of-customer-order-details__content">
        <OECOrderInfo customerOrder={customerOrder} customerOrderDescription={customerOrderDescription} />
      </div>
    );
  }

  const activeDeviceSubscriptions =
    customerOrder && activeEppDeviceSubscriptionsOfEmployee(customerOrder, deviceSubscriptions);
  const orderPendingApproval = customerOrder?.status === CustomerOrderStatus.PENDING_APPROVAL;
  const replacedSubscriptionDisplayId = customerOrder && findFirstReplacedSubscriptionDisplayId(customerOrder);

  return (
    <div className="of-customer-order-details__content">
      {customerOrder && <CustomerOrderInfo customerOrder={customerOrder} isApprover={true} />}
      {changeRequestDetails && <ChangeRequestInfo changeRequest={changeRequestDetails} mdmId={mdmId} />}
      {customerOrder && (
        <EppDeviceChangeSummaries customerOrder={customerOrder} deviceSubscriptions={deviceSubscriptions} />
      )}
      <EppPendingRedeemDisclaimers deviceSubscriptions={deviceSubscriptions} />
      {orderPendingApproval && activeDeviceSubscriptions && (
        <CustomerOrderDeviceSubscriptions
          subscriptions={activeDeviceSubscriptions}
          isEmployee={false}
          replacedSubscriptionDisplayId={replacedSubscriptionDisplayId}
          mdmId={mdmId}
        />
      )}
      {customerOrder && (
        <CustomerOrderOrderedProductsList
          pathTemplate={pathTemplate}
          customerOrder={customerOrder}
          isApprover={true}
          mdmId={mdmId}
        />
      )}
      {orderPendingApproval && (
        <>
          <CustomerOrderCustomerInfo ordererContact={ordererContact} />
          {billingAccounts && (
            <CustomerOrderDetailsForm
              billingAccounts={billingAccounts}
              customerOrder={customerOrder}
              ordererContact={ordererContact}
              isInternalCustomer={isInternalCustomer}
              mdmId={mdmId}
            />
          )}
        </>
      )}
    </div>
  );
};
